/**
 * The `next` parameter of /signin: where a logged-out visitor was going when
 * they were sent to sign in (a dashboard link in an email, an expired session).
 *
 * It is followed only when it is a path on this site. Anything else would turn
 * our sign-in page into an open redirect behind a link we sent.
 */

export const NEXT_PARAM = 'next'

const SIGNIN_PATH = '/signin'

// Any origin works: it only has to be one a relative path cannot escape.
const PROBE_ORIGIN = 'https://agami.invalid'

export const isSafeNextPath = (value: string | null | undefined): value is string => {
  if (!value || !value.startsWith('/')) return false

  // `//host` and `/\host` are protocol-relative to browsers; control characters
  // are stripped by them, so `/\t/host` would become `//host` too.
  if (value.startsWith('//') || value.includes('\\')) return false
  if (value.split('').some((char) => char.charCodeAt(0) < 0x20)) return false

  try {
    const url = new URL(value, PROBE_ORIGIN)

    if (url.origin !== PROBE_ORIGIN) return false

    // Back to /signin after signing in would bounce between the two routes.
    return url.pathname !== SIGNIN_PATH && !url.pathname.startsWith(`${SIGNIN_PATH}/`)
  } catch {
    return false
  }
}

/** The validated `next` of a query string, or null when absent or unsafe. */
export const getNextPath = (search: string): string | null => {
  const value = new URLSearchParams(search).get(NEXT_PARAM)

  return isSafeNextPath(value) ? value : null
}

/** /signin carrying `next` when it is safe, plain /signin otherwise. */
export const buildSigninPath = (next: string, params: Record<string, string> = {}): string => {
  const query = new URLSearchParams(params)

  if (isSafeNextPath(next)) query.set(NEXT_PARAM, next)

  const search = query.toString()

  return search ? `${SIGNIN_PATH}?${search}` : SIGNIN_PATH
}
